import { useState, useEffect } from 'react';
import { sawiaAPI, contactAPI } from '../../utils/api';
import PublicLayout from '../../components/layout/PublicLayout';
import { toast } from 'react-toastify';
import { MapPinIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';

// ══════════════════════════════════════════════════════════════
// CONTACT US PAGE  — ✅ Fixed: toast.success on send
// ══════════════════════════════════════════════════════════════
export function ContactPage() {
  const [sawias, setSawias]   = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);
  const [form, setForm]       = useState({
    sawia_id: '', sender_name: '', sender_email: '',
    sender_phone: '', subject: '', message: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    sawiaAPI.getAll().then(r => setSawias(r.data.data || []));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await contactAPI.send({ ...form, sawia_id: form.sawia_id || null });
      // ✅ Fixed: toast before state change so it's visible
      toast.success('Message sent! Jazakallahu Khairan. We will respond soon, in sha Allah.');
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setSent(false);
    setForm({ sawia_id: '', sender_name: '', sender_email: '', sender_phone: '', subject: '', message: '' });
  };

  if (sent) return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="text-6xl mb-5">✅</div>
        <h2 className="text-2xl font-bold text-navy-800 mb-3">Message Sent!</h2>
        <p className="text-gray-500 mb-2">Jazakallahu Khairan for reaching out. Your message has been received.</p>
        <p className="text-gray-400 text-sm mb-8">The assigned contact person for your selected location will respond to you soon, in sha Allah.</p>
        <button onClick={resetForm} className="btn-primary">Send Another Message</button>
      </div>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-10">
          <h1 className="section-title mb-2">Contact Us</h1>
          <p className="text-gray-500">Get in touch with your local Sawia branch or the central office</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 space-y-5">
              <h2 className="font-semibold text-navy-700 text-lg mb-4">Send a Message</h2>

              <div>
                <label className="form-label">Select a Sawia Location (optional)</label>
                <select className="form-input" value={form.sawia_id} onChange={e => set('sawia_id', e.target.value)}>
                  <option value="">— General / Central Office —</option>
                  {sawias.map(s => (
                    <option key={s.id} value={s.id}>{s.name} — {s.city}, {s.country}</option>
                  ))}
                </select>
                <p className="text-gray-400 text-xs mt-1">
                  Your message will be directed to the assigned contact person for that location.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Your Full Name *</label>
                  <input required className="form-input" value={form.sender_name} onChange={e => set('sender_name', e.target.value)} placeholder="e.g. Musa Ibrahim" />
                </div>
                <div>
                  <label className="form-label">Email Address *</label>
                  <input required type="email" className="form-input" value={form.sender_email} onChange={e => set('sender_email', e.target.value)} placeholder="your@email.com" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Phone Number</label>
                  <input type="tel" className="form-input" value={form.sender_phone} onChange={e => set('sender_phone', e.target.value)} placeholder="+234 800 000 0000" />
                </div>
                <div>
                  <label className="form-label">Subject *</label>
                  <input required className="form-input" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="e.g. Membership enquiry" />
                </div>
              </div>

              <div>
                <label className="form-label">Message *</label>
                <textarea required rows={5} className="form-input resize-none" value={form.message} onChange={e => set('message', e.target.value)} placeholder="Write your message here..." />
              </div>

              <button type="submit" disabled={sending} className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                {sending
                  ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                  : '📤 Send Message'
                }
              </button>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-navy-700 rounded-2xl p-6 text-white">
              <h3 className="font-bold mb-4 text-cyan-400">Our Locations</h3>
              <div className="space-y-4">
                {sawias.slice(0, 4).map(s => (
                  <div key={s.id} className="border-t border-white/10 pt-3">
                    <p className="font-semibold text-sm">{s.name}</p>
                    {s.name_ar && <p className="arabic text-cyan-300 text-sm">{s.name_ar}</p>}
                    {s.address && <div className="flex gap-1.5 mt-1"><MapPinIcon className="h-3.5 w-3.5 text-white/40 flex-shrink-0 mt-0.5" /><p className="text-white/60 text-xs">{s.address}</p></div>}
                    {s.phone   && <div className="flex gap-1.5 mt-0.5"><PhoneIcon className="h-3.5 w-3.5 text-white/40 flex-shrink-0" /><p className="text-white/60 text-xs">{s.phone}</p></div>}
                    {s.email   && <div className="flex gap-1.5 mt-0.5"><EnvelopeIcon className="h-3.5 w-3.5 text-white/40 flex-shrink-0" /><p className="text-white/60 text-xs">{s.email}</p></div>}
                  </div>
                ))}
                {sawias.length === 0 && <p className="text-white/50 text-sm">No locations listed yet.</p>}
              </div>
            </div>
            <PrayerTimesWidget />
          </div>

        </div>
      </div>
    </PublicLayout>
  );
}

// ══════════════════════════════════════════════════════════════
// PRAYER TIMES WIDGET (AlAdhan API — free, no key needed)
// ══════════════════════════════════════════════════════════════
function PrayerTimesWidget() {
  const [times, setTimes]     = useState(null);
  const [city, setCity]       = useState('Lagos');
  const [input, setInput]     = useState('Lagos');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [nextPrayer, setNextPrayer] = useState(null);

  const PRAYERS = ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
  const ARABIC  = { Fajr:'الفجر', Sunrise:'الشروق', Dhuhr:'الظهر', Asr:'العصر', Maghrib:'المغرب', Isha:'العشاء' };

  const fetchTimes = async (c) => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(`https://api.aladhan.com/v1/timingsByCity?city=${encodeURIComponent(c)}&country=Nigeria&method=3`);
      const data = await res.json();
      if (data.code !== 200) throw new Error('City not found');
      setTimes(data.data.timings);
      setCity(c);
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      let found = false;
      for (const p of PRAYERS) {
        const [h, m] = (data.data.timings[p] || '00:00').split(':').map(Number);
        if (h * 60 + m > nowMin) { setNextPrayer(p); found = true; break; }
      }
      if (!found) setNextPrayer('Fajr');
    } catch {
      setError('Could not load prayer times for this city.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTimes('Lagos'); }, []);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-navy-700">🕌 Prayer Times</h3>
        <span className="text-gray-400 text-xs">{city}</span>
      </div>
      <div className="flex gap-2 mb-4">
        <input
          className="form-input flex-1 py-2 text-sm"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchTimes(input)}
          placeholder="Enter city..."
        />
        <button onClick={() => fetchTimes(input)} className="btn-primary py-2 px-3 text-sm">Go</button>
      </div>
      {loading && <div className="text-center py-4"><div className="h-6 w-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" /></div>}
      {error && <p className="text-red-400 text-xs text-center">{error}</p>}
      {times && !loading && (
        <div className="space-y-1.5">
          {PRAYERS.map(p => (
            <div key={p} className={`flex items-center justify-between px-3 py-2 rounded-xl transition-all ${nextPrayer === p ? 'bg-navy-700 text-white' : 'bg-gray-50 text-navy-800'}`}>
              <div className="flex items-center gap-2">
                {nextPrayer === p && <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse inline-block" />}
                <span className="text-sm font-medium">{p}</span>
                <span className={`text-xs ${nextPrayer === p ? 'text-cyan-300' : 'text-gray-400'}`}>{ARABIC[p]}</span>
              </div>
              <span className={`text-sm font-bold ${nextPrayer === p ? 'text-cyan-400' : 'text-navy-600'}`}>{times[p]}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-gray-300 text-xs text-center mt-3">Powered by AlAdhan.com</p>
    </div>
  );
}

export default ContactPage;
